import {
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Flex,
  Form,
  Input,
  Row,
  Space,
  Tag
} from 'antd'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { usePreScreeningServiceGetRiskGroupPersonEndpoint } from 'src/api/queries'
import { Permissions, PersonScreeningStatus } from 'src/api/requests'
import { ArrowDown } from 'src/assets'
import {
  CountrySelect,
  CUATMTreeSelect,
  GenderSelect,
  LoadingScreen,
  PageBreadcrumbs,
  Text
} from 'src/components'
import { useUser } from 'src/hooks'
import { dayMonthYearBackSlash, dayMonthYearFormatWithDot } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { getLocalizedTitle, toDayjs } from 'src/utils'

import { ChangeStatusModal } from '../components/ChangeStatusModal'
import { useStyles } from '../styles'
import { getPersonRiskGroupStatus } from '../utils'

export const PersonDetailsPage = () => {
  const { id, riskId } = useParams()

  const { hasPermission } = useUser()
  const [isModalOpen, setIsModalOpen] = useState(false)

  const [form] = Form.useForm()

  const { styles } = useStyles()
  const { t, currentLang } = useLocales()

  const { data, isLoading } = usePreScreeningServiceGetRiskGroupPersonEndpoint({
    personId: String(id),
    riskGroupId: String(riskId)
  })

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        ...data?.person,
        birthDate: toDayjs(data?.person?.birthDate),
        currentAddress: {
          ...data?.person?.currentAddress,
          cuatmLocalityId: getLocalizedTitle({
            item: data?.person?.currentAddress?.cuatmLocality,
            currentLang: currentLang.value,
            t
          })
        },
        residenceAddress: {
          ...data?.person?.residenceAddress,
          cuatmLocalityId: getLocalizedTitle({
            item: data?.person?.residenceAddress?.cuatmLocality,
            currentLang: currentLang.value,
            t
          })
        }
      })
    }
  }, [data])

  const screeningStatus = getPersonRiskGroupStatus(
    t,
    data?.screeningStatus ?? PersonScreeningStatus.NEW
  )

  const isSupport = hasPermission(
    Permissions.PRE_SCREENING_RISK_GROUPS_SUPPORT_ALL
  )

  const canChangeStatus =
    isSupport && data?.screeningStatus !== PersonScreeningStatus.PRE_SCREENING

  if (isLoading) {
    return <LoadingScreen />
  }

  return (
    <PageBreadcrumbs
      title={`${t('common.person')} - ${data?.person?.lastName} ${data?.person?.firstName}`}
      items={[
        {
          title: t('common.person'),
          path: paths.riskGroups.details(String(riskId))
        },
        {
          title: `${t('common.person')} - ${data?.person?.lastName} ${data?.person?.firstName}`
        }
      ]}
    >
      <Flex vertical gap={24}>
        <Flex vertical className="full-width">
          <Flex gap={16}>
            <Card className="full-width">
              <Flex vertical gap={8}>
                <Flex justify="space-between">
                  <Space>
                    <Text weight={400}>
                      {t('pre_screening.person_registered_from')}:
                    </Text>
                    <Text strong>
                      {data?.addedBy?.lastName} {data?.addedBy?.firstName}
                    </Text>{' '}
                  </Space>
                  <Button type="link">
                    <Tag
                      className={styles.statusTag}
                      onClick={() => canChangeStatus && setIsModalOpen(true)}
                      color={screeningStatus.color}
                    >
                      <Flex gap={4} justify="center" align="center">
                        <Text size="lg">{screeningStatus.text} </Text>
                        <ArrowDown
                          style={{
                            width: 16,
                            height: 16
                          }}
                        />
                      </Flex>
                    </Tag>
                  </Button>
                </Flex>

                {data?.screeningDate && (
                  <Space>
                    <Flex gap={8}>
                      <Text weight={400}>
                        {t('pre_screening.screening_day')}:
                      </Text>
                      <Text strong>
                        {dayjs
                          .utc(data?.screeningDate)
                          .tz(dayjs.tz.guess())
                          .format(dayMonthYearBackSlash)}
                      </Text>
                    </Flex>
                  </Space>
                )}
              </Flex>
            </Card>
          </Flex>
        </Flex>

        <Card>
          <Card title={t('cases.personal_data')}>
            <Form layout="vertical" form={form} disabled>
              <Row gutter={[16, 24]}>
                <Col xs={24} lg={6}>
                  <Form.Item name="lastName" label={t('form.last_name')}>
                    <Input placeholder={t('form.last_name')} />
                  </Form.Item>
                </Col>
                <Col xs={24} lg={6}>
                  <Form.Item name="firstName" label={t('form.first_name')}>
                    <Input placeholder={t('form.first_name')} />
                  </Form.Item>
                </Col>
                <Col xs={24} lg={6}>
                  <Form.Item name="simeCode" label={t('common.sime_code')}>
                    <Input placeholder={t('common.sime_code')} />
                  </Form.Item>
                </Col>
                <Col xs={24} lg={6}>
                  <Form.Item name="tarvCode" label={t('common.tarv_code')}>
                    <Input placeholder={t('common.tarv_code')} />
                  </Form.Item>
                </Col>
                <Col xs={24} lg={6}>
                  <Form.Item name="citizenship" label={t('common.citizenship')}>
                    <CountrySelect
                      popupMatchSelectWidth
                      placeholder={t('common.citizenship')}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} lg={6}>
                  <Form.Item name="idnp" label={t('form.idnp')}>
                    <Input placeholder={t('form.idnp')} />
                  </Form.Item>
                </Col>
                <Col xs={24} lg={6}>
                  <Form.Item name="birthDate" label={t('form.birth_date')}>
                    <DatePicker
                      className="full-width"
                      placeholder={t('form.birth_date')}
                      format={dayMonthYearFormatWithDot}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} lg={6}>
                  <Form.Item name="gender" label={t('form.gender')}>
                    <GenderSelect />
                  </Form.Item>
                </Col>
                <Col span={24} lg={12}>
                  <Row gutter={[16, 10]}>
                    <Col span={24}>
                      <Form.Item
                        name={['currentAddress', 'country']}
                        label={t('form.address')}
                      >
                        <CountrySelect
                          disabled
                          placeholder={t('common.country')}
                        />
                      </Form.Item>
                    </Col>

                    <Col span={24}>
                      <Form.Item name={['currentAddress', 'cuatmLocalityId']}>
                        <CUATMTreeSelect
                          placeholder={t('organizational_structure.city')}
                        />
                      </Form.Item>
                    </Col>

                    <Col span={24}>
                      <Form.Item name={['currentAddress', 'street']}>
                        <Input placeholder="bul. Moscovei" />
                      </Form.Item>
                    </Col>

                    <Col span={12}>
                      <Form.Item name={['currentAddress', 'streetNumber']}>
                        <Input placeholder="5/5" />
                      </Form.Item>
                    </Col>

                    <Col span={12}>
                      <Form.Item name={['currentAddress', 'apartmentNumber']}>
                        <Input placeholder="ap.90" />
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>
                <Col span={24} lg={12}>
                  <Row gutter={[16, 10]}>
                    <Col span={24}>
                      <Form.Item
                        name={['residenceAddress', 'country']}
                        label={t('cases.residential_address')}
                      >
                        <CountrySelect placeholder={t('common.country')} />
                      </Form.Item>
                    </Col>

                    {data?.person?.residenceAddress?.country === 'md' && (
                      <Col span={24}>
                        <Form.Item
                          name={['residenceAddress', 'cuatmLocalityId']}
                        >
                          <CUATMTreeSelect
                            placeholder={t('organizational_structure.city')}
                          />
                        </Form.Item>
                      </Col>
                    )}

                    <Col span={24}>
                      <Form.Item name={['residenceAddress', 'street']}>
                        <Input placeholder="bul. Moscovei" />
                      </Form.Item>
                    </Col>

                    <Col span={12}>
                      <Form.Item name={['residenceAddress', 'streetNumber']}>
                        <Input placeholder="5/5" />
                      </Form.Item>
                    </Col>

                    <Col span={12}>
                      <Form.Item name={['residenceAddress', 'apartmentNumber']}>
                        <Input placeholder="ap.90" />
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>
                <Col span={24}>
                  <Form.Item
                    valuePropName="checked"
                    name="isResidenceAddressSameAsCurrent"
                  >
                    <Checkbox>
                      {t('persons.residence_address_same_current_address')}
                    </Checkbox>
                  </Form.Item>
                </Col>
                <Col xs={24} lg={12}>
                  <Form.Item name="phone" label={t('common.phone')}>
                    <Input placeholder={t('common.phone')} />
                  </Form.Item>
                </Col>
                <Col xs={24} lg={12}>
                  <Form.Item name="email" label={t('form.email_address')}>
                    <Input placeholder={t('form.email_address')} />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>
        </Card>
      </Flex>

      <ChangeStatusModal
        personId={id}
        open={isModalOpen}
        onToggle={() => setIsModalOpen((prev) => !prev)}
      />
    </PageBreadcrumbs>
  )
}
